/**
 * ChannelsGrid
 *
 * Renderiza grid de cards delegando para cada plugin do CHANNEL_REGISTRY.
 * Plugins disponíveis renderizam via useStatus + renderCard.
 * Plugins indisponíveis renderizam via renderCard estático ("Em breve").
 */

import React, { useContext, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { IconBell, IconLock } from '@tabler/icons-react';
import { CHANNEL_REGISTRY } from '../../../channels/registry';
import UserContext from '../../../../../contexts/UserContext';
import EmptyChannelGrid from './EmptyChannelGrid';

// Canais que dependem de App Review da Meta. Enquanto não aprovado em produção,
// liberados apenas para accounts autorizadas (whitelist abaixo).
// WhatsApp Cloud API foi aprovado pela Meta e está liberado para todas as contas.
const META_RESTRICTED_CHANNELS = ['instagram', 'messenger'];
const META_ALLOWED_ACCOUNTS = new Set([7, 29, 283]);

const ComingSoonModal = ({ channel, onHide }) => (
  <Modal show={!!channel} onHide={onHide} centered size="sm">
    <Modal.Body style={{ padding: 32, textAlign: 'center' }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: channel?.gradient || '#f3f4f6',
          margin: '0 auto 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
        }}
      >
        {channel?.icon && React.createElement(channel.icon, { size: 28 })}
      </div>
      <h5 style={{ marginBottom: 8 }}>{channel?.name}</h5>
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Em desenvolvimento. Avisaremos quando estiver disponível.
      </p>
      <button
        type="button"
        onClick={onHide}
        style={{
          padding: '8px 16px',
          border: '1px solid #e5e7eb',
          background: '#fff',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        Entendi
      </button>
    </Modal.Body>
  </Modal>
);

const MetaLockedModal = ({ channel, onHide }) => (
  <Modal show={!!channel} onHide={onHide} centered size="sm">
    <Modal.Body style={{ padding: 32, textAlign: 'center' }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#fef3c7',
          margin: '0 auto 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#b45309',
        }}
      >
        <IconLock size={28} />
      </div>
      <h5 style={{ marginBottom: 8 }}>{channel?.name}</h5>
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Aguardando liberação Meta. Este canal está em revisão e será habilitado
        para todas as contas em breve.
      </p>
      <button
        type="button"
        onClick={onHide}
        style={{
          padding: '8px 16px',
          border: '1px solid #e5e7eb',
          background: '#fff',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        Entendi
      </button>
    </Modal.Body>
  </Modal>
);

const ChannelsGrid = ({ agentId, filterAlertOnly, onSelect, onConnect }) => {
  const { user } = useContext(UserContext);
  const accountId = Number(user?.account_id);
  const isMetaUnlocked = META_ALLOWED_ACCOUNTS.has(accountId);

  const [comingSoonChannel, setComingSoonChannel] = useState(null);
  const [metaLockedChannel, setMetaLockedChannel] = useState(null);

  const isMetaLocked = (channel) =>
    META_RESTRICTED_CHANNELS.includes(channel.id) && !isMetaUnlocked;

  const handleCardClick = (channel) => {
    if (!channel.isAvailable) {
      setComingSoonChannel(channel);
      return;
    }
    if (isMetaLocked(channel)) {
      setMetaLockedChannel(channel);
      return;
    }
    // Plugins disponíveis decidem internamente se vão para detail ou connect
    // baseado no status. Por padrão, se houver renderConnectFlow e não conectado,
    // chama onConnect. Caso contrário, onSelect.
    onSelect(channel.id);
  };

  // Wrapper para passar callbacks aos cards
  const cardContext = {
    onConnect,
    onSelect,
  };

  if (CHANNEL_REGISTRY.length === 0) {
    return <EmptyChannelGrid />;
  }

  return (
    <>
      <div className="channels-grid">
        {CHANNEL_REGISTRY.map((channel) => {
          const locked = isMetaLocked(channel);
          return (
            <div
              key={channel.id}
              className={`channels-grid__item${locked ? ' channels-grid__item--locked' : ''}`}
              onClick={() => handleCardClick(channel)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleCardClick(channel)}
              style={locked ? { position: 'relative' } : undefined}
            >
              {channel.renderCard ? (
                channel.renderCard(agentId, cardContext)
              ) : (
                <DefaultChannelCard channel={channel} />
              )}
              {locked && (
                <div className="channels-grid__lock-overlay">
                  <span className="channels-grid__lock-badge">
                    <IconLock size={12} />
                    Aguardando liberação Meta
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ComingSoonModal channel={comingSoonChannel} onHide={() => setComingSoonChannel(null)} />
      <MetaLockedModal channel={metaLockedChannel} onHide={() => setMetaLockedChannel(null)} />
    </>
  );
};

// Card padrão usado para plugins sem renderCard customizado
const DefaultChannelCard = ({ channel }) => {
  const Icon = channel.icon;
  return (
    <div className="channel-card channel-card--default">
      <div
        className="channel-card__icon"
        style={{ background: channel.gradient || '#f3f4f6' }}
      >
        <Icon size={24} style={{ color: channel.color || '#6b7280' }} />
      </div>
      <div className="channel-card__body">
        <h5 className="channel-card__name">{channel.name}</h5>
        <p className="channel-card__description">{channel.description}</p>
      </div>
      {!channel.isAvailable && (
        <span className="channel-card__badge channel-card__badge--coming-soon">
          Em breve
        </span>
      )}
    </div>
  );
};

export default ChannelsGrid;
